import { type FC, useMemo } from "react";
import { Dialog } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useLinkQuery, useUpdateLinkMutation } from "@/partner/entities/links";
import {
  linkFormValuesToInput,
  rewardToFormValues,
  type LinkFormValues,
} from "../model";
import { LinkForm } from "./LinkForm";

interface EditLinkDialogProps {
  open: boolean;
  linkId: string | null;
  onClose: () => void;
  onCompleted?: () => void;
}

export const EditLinkDialog: FC<EditLinkDialogProps> = ({
  open,
  linkId,
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const { data, loading } = useLinkQuery({
    variables: { id: String(linkId) },
    skip: !linkId || !open,
  });

  const [updateLink, { loading: updating }] = useUpdateLinkMutation({
    refetchQueries: ["LinksList"],
  });

  const initialValues = useMemo((): LinkFormValues | undefined => {
    if (!data) return undefined;
    return {
      title: data.link.title ?? "",
      linkType: data.link.linkType,
      href: data.link.href ?? null,
      slug: data.link.slug ?? null,
      targetType: data.link.targetType ?? null,
      targetId: data.link.targetId ?? null,
      targetTitle: data.link.targetTitle ?? null,
      targetUrl: data.link.targetUrl ?? null,
      utmSource: data.link.utmSource ?? null,
      utmMedium: data.link.utmMedium ?? null,
      utmCampaign: data.link.utmCampaign ?? null,
      ...rewardToFormValues(data.link.reward),
    };
  }, [data]);

  const handleSubmit = async (values: LinkFormValues) => {
    await updateLink({
      variables: {
        id: String(linkId),
        input: linkFormValuesToInput(values),
      },
      onCompleted: () => {
        enqueueSnackbar(t("links.edit.success"), {
          variant: "success",
        });
        onCompleted?.();
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
    });
  };

  return (
    <Dialog fullWidth maxWidth="md" scroll="body" open={open} onClose={onClose}>
      <LinkForm
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onClose={onClose}
        title={t("links.edit.title")}
        loading={loading || updating}
      />
    </Dialog>
  );
};
